import type { ReactElement, ReactNode } from 'react';
import { useState } from 'react';
import { postMessage, WebViewMessage } from 'utils/webview';

import { FeatureFlag } from 'config';

import Address from 'models/Address';
import useAddresses from 'hooks/useAddresses';
import useCurrentUser from 'hooks/useCurrentUser';

import Modal from '../Modal';
import AddressModal from './AddressModal';
import AddressSelector from './AddressSelector';

type Props = {
  show: boolean;
  selectedId: number | null;
  onShow: () => void;
  onChange: (address: Address) => void;
  onClose: () => void;
  baseError?: string;
  bodyHeader?: ReactNode;
};

function ShipToModal({
  show,
  selectedId,
  onShow,
  onChange,
  onClose,
  baseError,
  bodyHeader,
}: Props): ReactElement {
  const { currentUser } = useCurrentUser();
  const { addresses, reload: reloadAddresses } = useAddresses();
  const [addressToEdit, setAddressToEdit] = useState(null);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState(selectedId);

  const shouldRenderRegionalDisclaimer = currentUser
    .hasFeatureEnabled(FeatureFlag.REGIONAL_DISCLAIMER);

  const openAddressModal = (address?) => {
    setAddressToEdit(address);
    onClose();
    setShowAddressModal(true);
  };

  const closeAddressModal = (address?) => {
    if (address) reloadAddresses();
    onShow();
    setShowAddressModal(false);
  };

  return (
    <>
      <Modal
        show={show}
        onClose={() => {
          postMessage(WebViewMessage.ADDRESS_MODAL_CLOSE);
          onClose();
        }}
        className="c-modal-ship-to"
        header={(
          <Modal.Header className="c-heading--brand-5" center={false}>
            Ship This Delivery To
          </Modal.Header>
        )}
        body={(
          <>
            {baseError && (
              <div className="mb-4 col-12 u-text--sm">
                <div className="c-alert c-alert--error" role="alert">
                  <div className="c-alert__text">
                    {baseError}
                  </div>
                </div>
              </div>
            )}
            {bodyHeader}
            <div>
              <div className="c-heading c-heading--brand-8">
                SELECT ADDRESS
              </div>
              {addresses && (
                <AddressSelector
                  addresses={addresses}
                    // @ts-expect-error TODO: fix null safety
                  selectedId={selectedAddressId}
                  onSelect={(address) => setSelectedAddressId(address.id)}
                  onEdit={openAddressModal}
                />
              )}
            </div>
            <hr />
            {shouldRenderRegionalDisclaimer ? (
              <div className="u-text--xs">
                Due to regional availability constraints, certain items may
                not be accessible in your newly selected location. We apologize
                for any inconvenience this may cause, please ensure to review
                your upcoming order.
              </div>
            ) : (
              <button
                type="button"
                className="c-button c-button--secondary d-block"
                onClick={() => openAddressModal()}
              >
                Add Address
              </button>
            )}
          </>
        )}
        footer={(
          <div
            className={shouldRenderRegionalDisclaimer
              ? 'd-flex align-items-center c-modal-ship-to__footer-buttons'
              : ''}
          >
            {shouldRenderRegionalDisclaimer && (
            <button
              type="button"
              className="c-button c-button--secondary"
              onClick={() => openAddressModal()}
            >
              Add Address
            </button>
            )}
            <button
              type="button"
              onClick={() => {
                const address = addresses.find((item) => (
                  item.id === selectedAddressId
                ));
                  // @ts-expect-error TODO: fix null safety
                onChange(address);
                postMessage(WebViewMessage.ADDRESS_MODAL_CHANGE, { address });
              }}
              className="btn btn-default c-button c-button--primary"
            >
              Ship to This Address
            </button>
          </div>
        )}
      />
      {showAddressModal && (
        <AddressModal
          initialAddress={addressToEdit ?? null}
          onClose={closeAddressModal}
        />
      )}
    </>
  );
}

export default ShipToModal;
